import { SadmEntityDisplay } from '@modules/sadm/models/sadm-entity-display';
import { Media, SadmEntity } from '@pixacare/domain';
import { FlattenedSequence } from '@pixacare/dtos';
import { pickLatestItem } from '@shared/utils/pick-latest-item.utils';
import { SadmGridAnalysis } from '../models/sadm-grid-analysis';
import { SadmGridEntity } from '../models/sadm-grid-entity';
import { SadmGridProtocol } from '../models/sadm-grid-protocol';

const toAnalysis = (sequences: FlattenedSequence[] | undefined): SadmGridAnalysis | null => {
  const latest = pickLatestItem(sequences ?? [], (sequence) => sequence.sequenceInstance.createdOn);
  if (!latest) {
    return null;
  }
  return {
    sequenceId: latest.sequenceInstance.id,
    formAnswer: latest.sequenceInstance.formAnswer ?? { answers: {} },
    createdOn: latest.sequenceInstance.createdOn,
    medias: latest.medias ?? [],
  };
};

const toLatestProtocol = (source: SadmEntityDisplay): SadmGridProtocol | null => {
  const latest = pickLatestItem(source.protocolsHistory ?? [], (protocol) => protocol.createdOn);
  if (!latest) {
    return null;
  }
  return { answer: latest.protocolCreationFormAnswer, createdOn: latest.createdOn };
};

/** Build the grid entity from a loaded server entity. */
export const toSadmGridEntity = (source: SadmEntityDisplay): SadmGridEntity => ({
  entityKey: crypto.randomUUID(),
  sadmEntityId: source.id,
  name: source.name,
  patientEntityIndex: source.patientEntityIndex,
  previewMedia: source.previewMedia,
  createdOn: source.createdOn,
  creationAnswer: source.entityCreationFormAnswer ?? { answers: {} },
  latestProtocol: toLatestProtocol(source),
  analysis: toAnalysis(source.sequences),
});

/** Build a fresh unsaved draft entity (no server id yet). */
export const createDraftGridEntity = (): SadmGridEntity => ({
  entityKey: crypto.randomUUID(),
  sadmEntityId: null,
  name: '',
  patientEntityIndex: 0,
  previewMedia: undefined,
  createdOn: new Date(),
  creationAnswer: { answers: {} },
  latestProtocol: null,
  analysis: null,
});

/** Adopt the server id (and freshly assigned name/index) after creation. */
export const applyServerEntity = (entity: SadmGridEntity, sadmEntity: SadmEntity): SadmGridEntity => ({
  ...entity,
  sadmEntityId: sadmEntity.id,
  name: sadmEntity.name,
  patientEntityIndex: sadmEntity.patientEntityIndex,
});

/** Set the latest analysis from a newly created sequence. */
export const applySequenceCreated = (entity: SadmGridEntity, sequence: FlattenedSequence): SadmGridEntity => ({
  ...entity,
  analysis: {
    sequenceId: sequence.sequenceInstance.id,
    formAnswer: sequence.sequenceInstance.formAnswer ?? { answers: {} },
    createdOn: sequence.sequenceInstance.createdOn,
    medias: sequence.medias ?? [],
  },
});

/** Apply an update to the latest analysis' medias (no-op when there is no analysis yet). */
export const applyAnalysisMedias = (entity: SadmGridEntity, updater: (medias: Media[]) => Media[]): SadmGridEntity =>
  entity.analysis ? { ...entity, analysis: { ...entity.analysis, medias: updater(entity.analysis.medias) } } : entity;
